import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import stickerMakerApi from '../../api/stickerMakerApi';
import { StickerInterface } from "../../store/sticker_maker/stickersSlice";
import stickerMakerUrls from "../../configs/sticker_maker/stickerMakerUrls";
import { stickerPackEntity, stickerPacksEntity } from "./stickerPacksActions";
import {StickerPackInterface} from "../../store/sticker_maker/stickerPacksSlice";

const PREFIX = 'sticker_maker';

const processStrategy = (value: any) => {
  return {
    ...value,
    image_url: stickerMakerUrls.remoteUrl(value.image_url)
  };
}

export const stickersEntity = new schema.Entity(
  'stickers',
  {
    sticker_pack: stickerPacksEntity
  },
  {
    processStrategy
  }
);

export const stickerEntity = new schema.Entity(
  'sticker',
  {
    sticker_pack: stickerPackEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      sticker_pack: { [key: string]: StickerPackInterface };
      sticker: StickerInterface;
    }
  >(response.data.sticker, stickerEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      sticker_pack: { [key: string]: StickerPackInterface };
      sticker: StickerInterface
    }
  >(response.sticker, stickerEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      sticker_packs: { [key: string]: StickerPackInterface };
      stickers: { [key: string]: StickerInterface },
    }
  >(response.data.stickers, [stickersEntity]);

  return {
    sticker_packs: normalized.entities?.sticker_packs || {},
    stickers: normalized.entities?.stickers || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

export const fetchStickers = createAsyncThunk(
  `${PREFIX}_stickers/fetchStickers`,
  async (params: object = {}) => {
    const response    = await stickerMakerApi.get(`/onesky/stickers`, { params })
    return normalizeCollection(response);
  }
);

export const fetchReviews = createAsyncThunk(
  `${PREFIX}_stickers/fetchStickers`,
  async (params: object = {}) => {
    const response    = await stickerMakerApi.get(`/onesky/stickers/reviews`, { params })
    return normalizeCollection(response);
  }
);

export const uploadSticker = createAsyncThunk(
  `${PREFIX}_stickers/createSticker`,
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createSticker = createAsyncThunk(
  `${PREFIX}_stickers/createSticker`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.post(`/onesky/stickers`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchSticker = createAsyncThunk(
  `${PREFIX}_stickers/fetchSticker`,
  async (id: number | string) => {
    const response = await stickerMakerApi.get(`/onesky/stickers/${id}`);
    return normalizeInstance(response);
  }
);

export const updateSticker = createAsyncThunk(
  `${PREFIX}_stickers/updateSticker`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/stickers/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroySticker = createAsyncThunk(
  `${PREFIX}_stickers/destroySticker`,
  async ({ id, stickerPackId }  : { id: number, stickerPackId: number }) => {
    await stickerMakerApi.delete(`/onesky/stickers/${id}`);
    return { id: id, stickerPackId: stickerPackId };
  }
);

export const blockSticker = createAsyncThunk(
  `${PREFIX}_stickers/updateSticker`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/stickers/${id}/block`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const approveSticker = createAsyncThunk(
  `${PREFIX}_stickers/updateSticker`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/stickers/${id}/approve`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const groupApproveSticker = createAsyncThunk(
  `${PREFIX}_stickers/updateStickers`,
  async (ids: any, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/stickers/approve`, { ids });
      return normalizeCollection(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const groupBlockSticker = createAsyncThunk(
  `${PREFIX}_stickers/updateStickers`,
  async (ids: any, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/stickers/block`, { ids });
      return normalizeCollection(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const groupDestroySticker = createAsyncThunk(
  `${PREFIX}_stickers/groupDestroyStickers`,
  async (ids: any, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/stickers/group_destroy`, { ids });
      return {
        ids: response.data.ids,
      };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
